import React, { useState, useMemo, useEffect } from 'react'
import { X, Upload } from 'lucide-react'
import { projectsApi, ParseAssetInventoryResult, AssetInventoryRow } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { useAuth } from '@/contexts/AuthContext'
import { canUpload as canUploadFiles, canManage } from '@/lib/roles'
import { useTranslation } from 'react-i18next'

interface Props {
  projectId: string
  onClose: () => void
  onSuccess: () => void
}

export const ImportAssetInventoryModal: React.FC<Props> = ({ projectId, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const { error: showError, success } = useToast()
  const { user } = useAuth()
  const canUpload = canUploadFiles(user)
  const canReplaceInventory = canManage(user)

  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<ParseAssetInventoryResult | null>(null)
  const [parsing, setParsing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  // Default replace=OFF. Project managers can explicitly opt in.
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [rows, setRows] = useState<AssetInventoryRow[]>([])

  useEffect(() => {
    if (!canUpload) {
      showError('Asset inventory import requires admin role')
      onClose()
    }
  }, [canUpload, showError, onClose])

  // ABOVE the early return, and they have to stay there. React counts the
  // hooks a component calls and the count must not change between renders.
  // canUpload is derived from `user`, and `user` goes null the moment a
  // 401 clears the session — so a modal that had rendered these two would
  // render without them on the very next pass and React would throw
  // "rendered fewer hooks than expected", taking the page with it. Both
  // read `rows`, which is state declared above; neither needs the guard.
  const stats = useMemo(() => {
    if (!rows.length) return null
    return {
      total: rows.length,
      matched: rows.filter(r => r.matched_project_system_id).length,
      unmatched: rows.filter(r => !r.matched_project_system_id).length,
    }
  }, [rows])

  const scopeStats = useMemo(() => {
    if (!rows.length) return null
    let inS = 0, out = 0, unc = 0, ai = 0, learned = 0
    for (const r of rows) {
      if (r.scope_status === 'in_scope') inS++
      else if (r.scope_status === 'out_of_scope') out++
      else unc++
      if (r.scope_source === 'ai') ai++
      else if (r.scope_source === 'learned') learned++
    }
    return { inS, out, unc, ai, learned }
  }, [rows])

  if (!canUpload) return null

  const handleFile = async (f: File | null) => {
    if (!f) return
    setFile(f)
    setResult(null)
    setParsing(true)
    try {
      const r = await projectsApi.parseAssetInventory(projectId, f)
      setResult(r)
      setRows(r.rows)
    } catch (err: any) {
      showError(err.message ?? t('importModal.parseFailed'))
      setFile(null)
    } finally {
      setParsing(false)
    }
  }

  const updateRowMatch = (idx: number, systemId: string | null) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, matched_project_system_id: systemId } : r))
  }

  // Item A: user overrides a scope verdict in the preview. marks the row
  // scope_source='human' so the commit persists it as the authoritative
  // learned outcome (last write wins)
  const updateRowScope = (idx: number, value: string) => {
    setRows(prev => prev.map((r, i) => {
      if (i !== idx) return r
      if (value === 'out') return { ...r, scope_status: 'out_of_scope', scope_function: null, scope_source: 'human', scope_confidence: null }
      if (value === 'unc') return { ...r, scope_status: 'uncertain', scope_function: null, scope_source: 'human', scope_confidence: null }
      const fn = value.startsWith('in:') ? value.slice(3) : null
      return { ...r, scope_status: 'in_scope', scope_function: fn, scope_source: 'human', scope_confidence: null }
    }))
  }

  // scope classification roll-up. only meaningful when the society had a
  // runtime function list (otherwise the pass didn't run)
  const scopeFunctions = result?.scope_functions ?? []
  const hasScope = scopeFunctions.length > 0
  const handleCommit = async () => {
    if (!file || !rows.length) return
    setCommitting(true)
    try {
      const res = await projectsApi.commitAssetInventory(projectId, {
        rows,
        source_filename: file.name,
        replace_existing: replaceExisting,
      })
      success(`${res.inserted} inventory items imported`)
      onSuccess()
    } catch (err: any) {
      showError(err.message ?? t('importModal.importFailed'))
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">{t('projectDetail.importInventory')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!result && (
            <div
              onDragEnter={e => { e.preventDefault(); setDragActive(true) }}
              onDragOver={e => { e.preventDefault() }}
              onDragLeave={e => { e.preventDefault(); setDragActive(false) }}
              onDrop={e => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0] ?? null) }}
              onClick={() => document.getElementById('inventory-file-input')?.click()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                           : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
              } ${parsing ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
              <div className="text-sm font-medium">
                {parsing ? t('importModal.parsing') : dragActive ? t('importModal.dropHere') : t('importModal.dropInventoryWorkbook')}
              </div>
              <div className="text-xs text-slate-400 mt-1">.xlsx with IACS Rec 190 format (sheet "3. Vessel Asset Inventory")</div>
              <input id="inventory-file-input" type="file" accept=".xlsx,.xls" className="hidden"
                     onChange={e => handleFile(e.target.files?.[0] ?? null)} />
            </div>
          )}

          {result && stats && (
            <>
              {result.telemetry && (
                result.telemetry.exampleRowsSkipped > 0 ||
                result.telemetry.emptyRowsSkipped   > 0 ||
                (result.telemetry.filledDownCells ?? 0) > 0 ||
                result.telemetry.stoppedAtRow != null
              ) && (
                <div className="text-[11px] text-slate-500 dark:text-slate-400 px-3 py-2 rounded-md bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700">
                  {(() => {
                    const t = result.telemetry!
                    const parts: string[] = [`Header detected on row ${t.headerRow}.`]
                    const s = (n: number) => n === 1 ? '' : 's'
                    if (t.exampleRowsSkipped > 0) {
                      const base = `Skipped ${t.exampleRowsSkipped} example row${s(t.exampleRowsSkipped)}`
                      parts.push(t.emptyRowsSkipped > 0
                        ? `${base} and ${t.emptyRowsSkipped} empty row${s(t.emptyRowsSkipped)}.`
                        : `${base}.`)
                    } else if (t.emptyRowsSkipped > 0) {
                      parts.push(`Skipped ${t.emptyRowsSkipped} empty row${s(t.emptyRowsSkipped)}.`)
                    }
                    const fd = t.filledDownCells ?? 0
                    if (fd > 0) parts.push(`Filled down ${fd} merged-cell value${s(fd)}.`)
                    if (t.stoppedAtRow != null) parts.push(`Import stopped at row ${t.stoppedAtRow} (10+ consecutive empty rows).`)
                    return parts.join(' ')
                  })()}
                </div>
              )}
              <div className="flex gap-3">
                <div className="flex-1 p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-center">
                  <div className="text-lg font-semibold">{stats.total}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{t('common.total')}</div>
                </div>
                <div className="flex-1 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 text-center">
                  <div className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">{stats.matched}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{t('importModal.autoMatched')}</div>
                </div>
                <div className="flex-1 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-center">
                  <div className="text-lg font-semibold text-amber-700 dark:text-amber-300">{stats.unmatched}</div>
                  <div className="text-[10px] text-slate-400 uppercase">{t('wordRa.unmapped')}</div>
                </div>
              </div>

              {hasScope && scopeStats && (
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="text-slate-400 uppercase tracking-wide">Scope</span>
                  <span className="px-2 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">{scopeStats.inS} in-scope</span>
                  <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">{scopeStats.out} out-of-scope</span>
                  <span className="px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">{scopeStats.unc} uncertain</span>
                  <span className="text-slate-400">
                    {scopeStats.learned > 0 && `${scopeStats.learned} learned`}
                    {scopeStats.learned > 0 && scopeStats.ai > 0 && ' · '}
                    {scopeStats.ai > 0 && `${scopeStats.ai} AI`}
                    {result?.scope_summary?.class_society && ` · ${result.scope_summary.class_society}`}
                  </span>
                </div>
              )}

              {canReplaceInventory && (
                <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300" title="Project managers only">
                  <input type="checkbox" checked={replaceExisting} onChange={e => setReplaceExisting(e.target.checked)} />
                  Replace existing inventory for this project
                </label>
              )}

              <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-900/40 sticky top-0">
                      <tr className="text-left text-[10px] text-slate-400 uppercase">
                        <th className="px-2 py-2 w-8">#</th>
                        <th className="px-2 py-2">System</th>
                        <th className="px-2 py-2">Equipment</th>
                        <th className="px-2 py-2">Vendor</th>
                        <th className="px-2 py-2 w-10">Cat</th>
                        {hasScope && <th className="px-2 py-2 w-52">Scope</th>}
                        <th className="px-2 py-2 w-48">Linked to</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <tr key={i} className={`border-t border-slate-100 dark:border-slate-700/50 ${
                          row.matched_project_system_id ? '' : 'bg-amber-50/40 dark:bg-amber-900/10'
                        }`}>
                          <td className="px-2 py-1.5 text-slate-400">{row.item_number ?? i + 1}</td>
                          <td className="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200">{row.system || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300 max-w-[200px] truncate">{row.equipment || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-500">{row.brand_manufacturer || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-500">{row.system_category || '—'}</td>
                          {hasScope && (
                            <td className="px-2 py-1.5">
                              <select
                                value={
                                  row.scope_status === 'in_scope' ? `in:${row.scope_function ?? ''}`
                                  : row.scope_status === 'out_of_scope' ? 'out'
                                  : 'unc'
                                }
                                onChange={e => updateRowScope(i, e.target.value)}
                                className={`w-full text-[10px] px-1.5 py-0.5 rounded border ${
                                  row.scope_status === 'in_scope'
                                    ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                                    : row.scope_status === 'out_of_scope'
                                      ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                                      : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                                }`}
                              >
                                <option value="unc">? uncertain</option>
                                <option value="out">✗ out of scope</option>
                                <optgroup label="In scope — function">
                                  {scopeFunctions.map(fn => (
                                    <option key={fn} value={`in:${fn}`}>✓ {fn}</option>
                                  ))}
                                </optgroup>
                              </select>
                              <div className="text-[9px] text-slate-400 mt-0.5 truncate" title={row.scope_reasoning ?? ''}>
                                {row.scope_source === 'ai' && `AI${typeof row.scope_confidence === 'number' ? ` ${Math.round(row.scope_confidence * 100)}%` : ''}`}
                                {row.scope_source === 'learned' && 'learned'}
                                {row.scope_source === 'matched' && 'matched'}
                                {row.scope_source === 'human' && 'you'}
                              </div>
                            </td>
                          )}
                          <td className="px-2 py-1.5">
                            <select
                              value={row.matched_project_system_id ?? ''}
                              onChange={e => updateRowMatch(i, e.target.value || null)}
                              className={`w-full text-[10px] px-1.5 py-0.5 rounded border ${
                                row.matched_project_system_id
                                  ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300'
                                  : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                              }`}
                            >
                              <option value="">— unlinked —</option>
                              {result.project_systems.map(ps => (
                                <option key={ps.id} value={ps.id}>
                                  {ps.system_name || ps.system_description || 'Unnamed'} ({ps.vendor_name_raw || '?'})
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <div className="text-[10px] text-slate-400">
            {result && `${file?.name} · ${stats?.total} items`}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={committing}
                    className="px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700">
              {t('common.cancel')}
            </button>
            {result && (
              <button onClick={handleCommit} disabled={committing}
                      className="px-3 py-1.5 text-xs bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded hover:opacity-90 flex items-center gap-1.5">
                {committing ? t('importModal.importing') : t('common.import')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
